import { VocabWord, ChatApiRequest, VocabApiRequest, VocabApiResponse } from '../types';
import { AI_CONFIG } from '../config/constants';
import { extractJSON, isProduction } from './helpers';
import { useMemo } from 'react';

// API response wrapper
class ApiClient {
  private baseUrl: string;
  private isDev: boolean;

  constructor() {
    this.baseUrl = window.location.origin;
    this.isDev = !isProduction();
  }

  private async request<T>(endpoint: string, options: RequestInit): Promise<T> {
    const url = this.isDev
      ? `${AI_CONFIG.API_URL}`
      : `${this.baseUrl}/api/${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth for direct API calls in dev
    if (this.isDev) {
      const apiKey = process.env.GEMINI_API_KEY || import.meta.env.VITE_GEMINI_API_KEY;
      if (!apiKey) {
        throw new Error('API密钥未配置');
      }
      (headers as any)['Authorization'] = `Bearer ${apiKey}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }

    return response.json();
  }

  // Vocab API calls
  async getVocabulary(theme: string): Promise<VocabWord[]> {
    try {
      let response: any;

      if (this.isDev) {
        // Direct API call in dev
        const systemPrompt = `You are a helpful assistant designed to teach English to Chinese children.
You MUST output a valid JSON object with a key 'words'.
'words' must be an array of 5 objects.
Each object must have these keys:
- "english": The English word (lowercase).
- "chinese": Chinese translation.
- "emoji": A relevant emoji.
- "hint": A simple English sentence with the word replaced by underscores (___).
- "hintChinese": Chinese translation of the hint sentence.
- "exampleSentence": A simple example sentence using the word (for children, CEFR A1/A2 level).
- "exampleSentenceChinese": Chinese translation of the example sentence.
Ensure the words are simple (CEFR Pre-A1/A1).`;

        const userPrompt = `Generate 5 English vocabulary words about: "${theme}". Please include both English and Chinese hints and example sentences for each word.`;

        const payload: ChatApiRequest = {
          model: AI_CONFIG.MODEL,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          stream: false,
          response_format: { type: 'json_object' },
          temperature: AI_CONFIG.TEMPERATURE
        };

        response = await this.request('chat/completions', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        // Use proxy in production
        response = await this.request('vocab', {
          method: 'POST',
          body: JSON.stringify({ theme }),
        });
      }

      const content = response.choices?.[0]?.message?.content || response;
      const parsed = JSON.parse(extractJSON(content));
      return parsed.words || parsed;
    } catch (error) {
      console.error('Vocab API Error:', error);
      throw error;
    }
  }

  // Chat API calls
  async sendChatMessage(messages: Array<{ role: string; content: string }>): Promise<string> {
    try {
      let response: any;

      if (this.isDev) {
        const payload: ChatApiRequest = {
          model: AI_CONFIG.MODEL,
          messages,
          stream: false,
          temperature: AI_CONFIG.TEMPERATURE
        };

        response = await this.request('chat/completions', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        response = await this.request('ai', {
          method: 'POST',
          body: JSON.stringify({
            model: AI_CONFIG.MODEL,
            messages,
            stream: false,
            temperature: AI_CONFIG.TEMPERATURE
          }),
        });
      }

      return response.choices[0]?.message?.content || '';
    } catch (error) {
      console.error('Chat API Error:', error);
      throw error;
    }
  }

  // TTS API calls
  async textToSpeech(text: string, voice: string = AI_CONFIG.VOICE): Promise<string | null> {
    try {
      const cleanedText = text.trim();
      if (!cleanedText) return null;

      let audioUrl: string | null = null;

      if (this.isDev) {
        // Direct TTS API call in dev would go here
        // For now, we'll fallback to browser TTS
        return null;
      } else {
        // Use proxy in production
        const response = await fetch(`${this.baseUrl}/api/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            input: cleanedText,
            voice: voice,
            speed: 0.9,
            pitch: 1,
            style: 'general',
            role: 'default'
          }),
        });

        if (!response.ok) {
          throw new Error(`TTS API 请求失败: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('audio')) {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        }

        const data = await response.json();
        audioUrl = data.url || data.audioUrl || data.audio || data.data || data.path || data;

        if (typeof audioUrl === 'string') {
          if (audioUrl.startsWith('//')) {
            audioUrl = 'https:' + audioUrl;
          }
          const audioResponse = await fetch(audioUrl);
          if (audioResponse.ok) {
            const audioBlob = await audioResponse.blob();
            return URL.createObjectURL(audioBlob);
          }
          return audioUrl;
        }

        throw new Error('TTS API 返回格式未知');
      }
    } catch (error) {
      console.error('TTS API Error:', error);
      return null;
    }
  }
}

// React hook for stable API client instance
export const useApiClient = () => {
  return useMemo(() => new ApiClient(), []);
};

// Legacy export for backwards compatibility
export const apiClient = new ApiClient();